import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useT } from "../hooks/useT";
import { useMagneticHover } from "../hooks/useMagneticHover";

export default function InquirySection() {
  const t = useT();
  const ref = useRef(null);
  const submitBtnRef = useMagneticHover(0.25, 60);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    full_name: "", organization: "", email: "", role: "", capital_interest: "", message: "",
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.08 }
    );
    ref.current?.querySelectorAll(".fade-in, .clip-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Auto-dismiss error after 6 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.organization || !form.email || !form.role) return;
    setLoading(true);
    setError(null);
    try {
      await axios.post(`/api/inquiry`, form);
      setSubmitted(true);
    } catch (err) {
      console.error("Inquiry submission failed:", err);
      const message = err.response?.data?.error || "Something went wrong. Please try again or email us directly.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 0,
    padding: '12px 0',
    color: 'var(--text-primary)',
    fontSize: '15px',
    fontFamily: 'Manrope, sans-serif',
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.3s',
  };
  const labelClass = "font-sans text-[11px] uppercase tracking-[0.15em] text-[var(--text-tertiary)] block mb-2";

  return (
    <section id="inquiry" ref={ref} data-testid="inquiry-section" className="relative py-32 md:py-44 bg-[var(--bg-surface)] border-t" style={{ borderColor: 'var(--rule)' }}>
      <div className="max-w-2xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="fade-in text-center mb-20">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-px h-4" style={{ backgroundColor: 'var(--gold)', opacity: 0.3 }} />
            <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-[var(--text-tertiary)]">04</p>
          </div>
          <h2
            data-testid="inquiry-heading"
            className="clip-reveal font-serif text-3xl sm:text-4xl tracking-[0.06em] text-[var(--text-primary)] mb-8"
            style={{ fontWeight: 300 }}
          >
            {t.inquiry.heading}
          </h2>
          <p className="font-sans text-[15px] text-[var(--text-secondary)] leading-[1.75] mb-4">{t.inquiry.intro}</p>
          <p className="font-sans text-[13px] text-[var(--text-tertiary)] leading-relaxed">{t.inquiry.disclaimer}</p>
        </div>

        {/* Error Toast */}
        {error && (
          <div
            className="mb-8 px-5 py-4 border text-center transition-all duration-500"
            style={{
              borderColor: 'rgba(200, 80, 80, 0.3)',
              backgroundColor: 'rgba(200, 80, 80, 0.06)',
              animation: 'fadeSlideIn 0.4s ease-out',
            }}
          >
            <p className="font-sans text-[13px] text-[#c85050] leading-relaxed">{error}</p>
          </div>
        )}

        {/* Form */}
        {submitted ? (
          <div data-testid="inquiry-success-message" className="fade-in visible text-center py-16">
            <div className="mb-6">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1" className="mx-auto" style={{ opacity: 0.7 }}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <p className="font-sans text-[15px] text-[var(--text-primary)] leading-[1.75] mb-3">{t.inquiry.success}</p>
            <p className="font-sans text-[12px] text-[var(--text-tertiary)]">
              A confirmation has been sent to your email.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="inquiry-form" className="fade-in space-y-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
              <div>
                <label className={labelClass}>{t.inquiry.fields.fullName} *</label>
                <input data-testid="inquiry-full-name" required value={form.full_name}
                  onChange={(e) => handleChange("full_name", e.target.value)}
                  style={inputStyle} />
              </div>
              <div>
                <label className={labelClass}>{t.inquiry.fields.organization} *</label>
                <input data-testid="inquiry-organization" required value={form.organization}
                  onChange={(e) => handleChange("organization", e.target.value)}
                  style={inputStyle} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
              <div>
                <label className={labelClass}>{t.inquiry.fields.email} *</label>
                <input type="email" data-testid="inquiry-email" required value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  style={inputStyle} />
              </div>
              <div>
                <label className={labelClass}>{t.inquiry.fields.role} *</label>
                <select data-testid="inquiry-role-select" required value={form.role}
                  onChange={(e) => handleChange("role", e.target.value)}
                  style={{ ...inputStyle, appearance: 'none', cursor: 'pointer', color: form.role ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                  <option value="" disabled>{t.inquiry.fields.roleSelect}</option>
                  {t.inquiry.roles.map((r) => (
                    <option key={r} value={r} style={{ background: '#0f0f0f', color: '#e8e6e3' }}>{r}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>{t.inquiry.fields.capitalInterest}</label>
              <input data-testid="inquiry-capital" value={form.capital_interest}
                onChange={(e) => handleChange("capital_interest", e.target.value)}
                style={inputStyle} />
            </div>

            <div>
              <label className={labelClass}>{t.inquiry.fields.message}</label>
              <textarea data-testid="inquiry-message" rows={3} value={form.message}
                onChange={(e) => handleChange("message", e.target.value)}
                style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }} />
            </div>

            <div className="pt-6" ref={submitBtnRef}>
              <button type="submit" disabled={loading} data-testid="inquiry-submit-button"
                className="wipe-btn w-full py-4 border font-sans text-[11px] uppercase tracking-[0.22em] disabled:opacity-40"
                style={{ borderColor: 'var(--text-primary)', color: 'var(--text-primary)', background: 'transparent', borderRadius: 0 }}>
                {loading ? t.inquiry.submitting : t.inquiry.submit}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
